/**
 * Minimal cookie helpers — read by name, write with max-age, refresh on each
 * read for sliding-window lifetimes. No third-party dep so the loader bundle
 * stays tight.
 *
 * Caveats baked into the defaults:
 * - `path=/` so the cookie is visible to every route on the host page.
 * - `SameSite=Lax` so the cookie survives top-level navigations the buyer
 *   initiates (e.g. clicking a link) but blocks cross-site request forgery.
 *   `None` would require third-party cookie scope which Safari ITP nukes.
 * - `Secure` is auto-set on https pages. Skipped on http://localhost so the
 *   sandbox works without TLS.
 * - No `Domain=` attribute. The cookie defaults to the exact host that wrote
 *   it. Cross-host handoff (e.g. dealer.com → checkout.ekho.com) is done via
 *   query params in `appendAttribution`, not shared cookies.
 */

const COOKIE_DEFAULTS = {
  path: '/',
  sameSite: 'Lax' as const,
};

function isSecureOrigin(): boolean {
  return typeof window !== 'undefined' && window.location.protocol === 'https:';
}

/** Read a cookie value by name, or `null` when absent / storage unavailable. */
export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const target = `${name}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(target)) {
      try {
        return decodeURIComponent(part.slice(target.length));
      } catch {
        return null;
      }
    }
  }
  return null;
}

/**
 * Write a cookie with a max-age in seconds. Idempotent — call repeatedly to
 * implement sliding windows (each write refreshes the expiry).
 */
export function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === 'undefined') return;
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    `Max-Age=${maxAgeSeconds}`,
    `Path=${COOKIE_DEFAULTS.path}`,
    `SameSite=${COOKIE_DEFAULTS.sameSite}`,
  ];
  if (isSecureOrigin()) parts.push('Secure');
  document.cookie = parts.join('; ');
}

/**
 * Read a cookie, minting + persisting a fresh value via `factory` when absent
 * or empty. An empty-string cookie counts as absent so a corrupt/blank value
 * can't pin itself forever — otherwise we'd keep writing the same empty
 * `ekho_aid` / `ekho_sid` on every bootstrap and break identity joins.
 * Either branch writes the cookie so the expiry is refreshed (sliding window)
 * on every call.
 */
export function readOrCreateCookie(
  name: string,
  factory: () => string,
  maxAgeSeconds: number,
): string {
  const existing = readCookie(name);
  const value = existing && existing.length > 0 ? existing : factory();
  writeCookie(name, value, maxAgeSeconds);
  return value;
}
